import React from 'react';
import { useTranslation } from 'react-i18next';
import { AppBar, Box, IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { Menu as MenuIcon, AccountCircle } from '@mui/icons-material';
import BuildingIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Building.js';
import { useUser, userManager, beginLogoutSession } from '../OidcProvider/OidcProvider';

/**
 * DummyNavBar - Top header for local development.
 * Navigation links live in LocalDevSideNav (side panel); this bar provides org branding and profile menu.
 */
export interface DummyNavBarProps {
  isSideNavOpen: boolean;
  onSideNavToggle: () => void;
}

export function DummyNavBar({ isSideNavOpen, onSideNavToggle }: DummyNavBarProps) {
  const { t } = useTranslation();
  const { userProfile } = useUser();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const appBarRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const appBar = appBarRef.current;
    if (!appBar) {
      return;
    }

    const syncHeaderHeight = () => {
      document.documentElement.style.setProperty(
        '--atlas-global-header-height',
        `${appBar.offsetHeight}px`,
      );
    };

    syncHeaderHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncHeaderHeight);
      return () => {
        window.removeEventListener('resize', syncHeaderHeight);
      };
    }

    const resizeObserver = new ResizeObserver(syncHeaderHeight);
    resizeObserver.observe(appBar);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    beginLogoutSession();
    try {
      await userManager.removeUser();

      await userManager.signoutRedirect({
        post_logout_redirect_uri: window.location.origin,
      });
    } catch (error) {
      console.error('Error during logout:', error);
      await userManager.removeUser();
      window.location.href = '/';
    }
  };

  if (!userProfile) {
    return null;
  }

  return (
    <AppBar
      ref={appBarRef}
      position="static"
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        overflow: 'visible',
        zIndex: (theme) => theme.zIndex.modal + 5,
      }}
    >
      <Toolbar>
        <IconButton
          size="large"
          edge="start"
          color="inherit"
          aria-label={t('nav.local_dev.toggle_side_nav')}
          aria-controls="local-dev-side-nav"
          aria-expanded={isSideNavOpen}
          onClick={onSideNavToggle}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1, flexWrap: 'wrap' }}>
          <BuildingIcon style={{ width: 32, height: 32, marginRight: 8, color: '#1976d2' }} />
          <Typography variant="h6" component="div">
            Local Dev Org
          </Typography>
        </Box>
        <IconButton
          size="large"
          edge="end"
          aria-label="account of current user"
          aria-controls="profile-menu"
          aria-haspopup="true"
          onClick={handleProfileMenuOpen}
          color="inherit"
        >
          <AccountCircle />
        </IconButton>
        <Menu
          id="profile-menu"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          slotProps={{
            paper: {
              sx: { zIndex: (theme) => theme.zIndex.modal + 10 },
            },
          }}
        >
          <MenuItem disabled>
            <Typography variant="body1">{userProfile.name}</Typography>
          </MenuItem>
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">
              {userProfile.email}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}
